import { type ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { SearchIcon, XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Input, type InputVariants } from '@air/primitive-input';

export type SimpleSearchInputProps = {
  value: string;
  onSearchChange: (value: string) => void;
  placeholder?: string;
  inputSize?: InputVariants['size'];
  'aria-label'?: string;
  'data-testid'?: string;
};

const SIMPLE_SEARCH_INPUT_DEBOUNCE_MS = 300;
const SIMPLE_SEARCH_INPUT_CLEAR_BUTTON_TEST_ID = 'SIMPLE_SEARCH_INPUT_CLEAR_BUTTON';

export const SimpleSearchInput = memo(
  ({
    value,
    onSearchChange,
    placeholder,
    inputSize = 'large',
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
  }: SimpleSearchInputProps) => {
    const [search, setSearch] = useState(value);

    const [debouncedSearch] = useDebouncedCallback(onSearchChange, SIMPLE_SEARCH_INPUT_DEBOUNCE_MS, {
      trailing: true,
    });

    useEffect(() => {
      setSearch(value);
    }, [value]);

    const handleSearchChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSearch(value);
      },
      [debouncedSearch],
    );

    const handleClearSearch = useCallback(() => {
      setSearch('');
      debouncedSearch('');
    }, [debouncedSearch]);

    return (
      <div className="relative flex w-full">
        <SearchIcon size="md" className="absolute top-1/2 left-2 -translate-y-1/2 ads:text-icon-default text-gray-10" />
        <Input
          aria-label={ariaLabel}
          className="w-full px-8"
          data-testid={dataTestId}
          onChange={handleSearchChange}
          placeholder={placeholder}
          size={inputSize}
          value={search}
        />
        {!!search && (
          <Button
            appearance="outline"
            className="absolute top-1/2 right-2 -translate-y-1/2"
            color="gray"
            data-testid={SIMPLE_SEARCH_INPUT_CLEAR_BUTTON_TEST_ID}
            onClick={handleClearSearch}
            prefix={<XIcon size="xs" />}
            size="small"
          >
            Clear
          </Button>
        )}
      </div>
    );
  },
);

SimpleSearchInput.displayName = 'SimpleSearchInput';
